import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  CircularProgress,
} from '@mui/material';
import { MaintenanceRecordCreate } from '../../../../types/api';
import { useMaintenanceFormState } from './hooks';
import {
  MaintenanceFormFields,
  MaintenanceDateFields,
  MaintenanceFileUpload,
  MaintenanceFutureFields,
} from './components';
import { useErrorHandler } from '../../../../hooks/useErrorHandler';

interface CreateMaintenanceRecordDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (record: MaintenanceRecordCreate) => Promise<void>;
  droneSerial: string;
  loading: boolean;
  error: string | null;
  selectedFiles: File[];
  onFileSelect: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onRemoveFile: (index: number) => void;
  uploadingFiles: boolean;
}

export const CreateMaintenanceRecordDialog: React.FC<CreateMaintenanceRecordDialogProps> = ({
  open,
  onClose,
  onSubmit,
  droneSerial,
  loading,
  error,
  selectedFiles,
  onFileSelect,
  onRemoveFile,
  uploadingFiles,
}) => {
  // Use extracted form state hook
  const {
    record,
    maintenanceTypes,
    loadingTypes,
    organizationUsers,
    loadingUsers,
    handleFieldChange,
    isFormValid,
    addMaintenanceType,
  } = useMaintenanceFormState({ open, droneSerial });

  const { showError, showSuccess } = useErrorHandler();

  const handleSubmit = async () => {
    if (!record.work_description?.trim()) {
      return; // Validation handled by disabled state
    }

    try {
      await onSubmit(record);
      showSuccess('Maintenance record created successfully');
      onClose();
    } catch (err) {
      showError(err, 'Failed to create maintenance record');
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      data-testid="maintenance-create-dialog"
    >
      <DialogTitle>Add Maintenance Record</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <MaintenanceFormFields
          record={record}
          maintenanceTypes={maintenanceTypes}
          loadingTypes={loadingTypes}
          organizationUsers={organizationUsers}
          loadingUsers={loadingUsers}
          onFieldChange={handleFieldChange}
          onAddMaintenanceType={addMaintenanceType}
        />

        <MaintenanceDateFields record={record} onFieldChange={handleFieldChange} />

        <MaintenanceFileUpload
          selectedFiles={selectedFiles}
          onFileSelect={onFileSelect}
          onRemoveFile={onRemoveFile}
          uploadingFiles={uploadingFiles}
        />

        <MaintenanceFutureFields />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          data-testid="maintenance-create-submit"
          onClick={handleSubmit}
          variant="contained"
          disabled={!isFormValid || loading || loadingTypes || uploadingFiles}
        >
          {loading || uploadingFiles ? (
            <>
              <CircularProgress size={20} sx={{ mr: 1 }} />
              {uploadingFiles ? 'Uploading...' : 'Creating...'}
            </>
          ) : (
            'Create Maintenance Record'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
